// @intent: Own cleanup for one generated setup(api) execution
// @flow: PixiRuntimeCanvas creates scope -> PlayableAPI registers handlers/tweens -> effect cleanup disposes scope
// @stale: Extend when PlayableAPI gains runtime-owned scheduler handles

import type { PlayableTweenHandle } from '@mintyads/playable-api';

export interface RuntimeCleanupScope {
  add(cleanup: () => void): void;
  trackCleanup(cleanup: () => void): () => void;
  trackTween(handle: PlayableTweenHandle): PlayableTweenHandle;
  dispose(): void;
}

function once(cleanup: () => void): () => void {
  let called = false;
  return () => {
    if (called) return;
    called = true;
    cleanup();
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function createRuntimeCleanupScope(): RuntimeCleanupScope {
  const cleanups = new Set<() => void>();
  const tweens = new Set<PlayableTweenHandle>();
  let disposed = false;

  const runCleanup = (cleanup: () => void, errors: unknown[]): void => {
    try {
      cleanup();
    } catch (error) {
      errors.push(error);
    }
  };

  const scope: RuntimeCleanupScope = {
    add(cleanup) {
      scope.trackCleanup(cleanup);
    },
    trackCleanup(cleanup) {
      const tracked = once(cleanup);
      if (disposed) {
        tracked();
        return tracked;
      }
      cleanups.add(tracked);
      return tracked;
    },
    trackTween(handle) {
      if (disposed) {
        handle.stop();
        return handle;
      }
      tweens.add(handle);
      void handle.finished
        .finally(() => {
          tweens.delete(handle);
        })
        .catch(() => undefined);
      return handle;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      const errors: unknown[] = [];
      for (const tween of [...tweens]) tween.stop();
      tweens.clear();
      for (const cleanup of [...cleanups]) runCleanup(cleanup, errors);
      cleanups.clear();
      if (errors.length > 0) {
        throw new Error(`runtime cleanup failed: ${errors.map(errorMessage).join('; ')}`);
      }
    },
  };

  return scope;
}
